import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
// src/components/settings/DataPrivacyTab.tsx
import React, { useState } from "react";
import {
  Card,
  Grid,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { supabase } from "../../lib/supabase";
import { emailService, EmailConfig } from "../../services/emailService";
import { clearUserPersistence } from "../../lib/persistenceIdentity";
import {
  clearActiveUserLocalData,
  type LocalDataClearReceipt,
} from "../../lib/localDataClear";
import { useQueryClient } from "@tanstack/react-query";

interface DataPrivacyTabProps {
  identityUserId: string;
  emailConfig: EmailConfig;
  onShowSnackbar: (
    message: string,
    severity: "success" | "error" | "info" | "warning"
  ) => void;
  onReloadSettings: () => void;
  isAdmin?: boolean;
}

const DataPrivacyTab: React.FC<DataPrivacyTabProps> = ({
  identityUserId,
  emailConfig,
  onShowSnackbar,
  onReloadSettings,
  isAdmin = false,
}) => {
  const queryClient = useQueryClient();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [clearReceipt, setClearReceipt] =
    useState<LocalDataClearReceipt | null>(null);
  const [clearingLocalData, setClearingLocalData] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);

  const exportData = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const [clientsRes, leadsRes, activitiesRes, searchesRes] =
        await Promise.all([
          supabase.from("clients").select("*").eq("user_id", user.id),
          supabase.from("leads").select("*").eq("user_id", user.id),
          supabase.from("activities").select("*").eq("user_id", user.id),
          supabase.from("saved_searches").select("*").eq("user_id", user.id),
        ]);

      const exportDataObj = {
        exportDate: new Date().toISOString(),
        user: { id: user.id, email: user.email },
        clients: clientsRes.data || [],
        leads: leadsRes.data || [],
        activities: activitiesRes.data || [],
        savedSearches: searchesRes.data || [],
        settings: { emailConfig },
      };

      const blob = new Blob([JSON.stringify(exportDataObj, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `locatenow-export-${
        new Date().toISOString().split("T")[0]
      }.json`;
      a.click();
      URL.revokeObjectURL(url);

      onShowSnackbar("Data exported successfully!", "success");
    } catch (error) {
      console.error("Export error:", error);
      onShowSnackbar("Failed to export data", "error");
    }
  };

  const clearLocalData = async () => {
    if (clearingLocalData) return;
    setClearingLocalData(true);
    setShowClearDialog(false);
    try {
      // Cancel remote completions before advancing the local persistence
      // generation, then remove cached server rows after the storage clear.
      await queryClient.cancelQueries();
      const receipt = clearActiveUserLocalData(identityUserId);
      queryClient.clear();
      emailService.resetUserConfig();
      emailService.setUserId(identityUserId);
      setClearReceipt(receipt);

      if (!receipt.complete) {
        onShowSnackbar(
          `Local clear was incomplete (${receipt.failedKeys.length} item${receipt.failedKeys.length === 1 ? "" : "s"} could not be verified).`,
          "error"
        );
        return;
      }

      onReloadSettings();
      onShowSnackbar(
        receipt.durability === "durable"
          ? `Cleared and verified ${receipt.removedCount} local item${receipt.removedCount === 1 ? "" : "s"}.`
          : `Cleared ${receipt.removedCount} current-tab item${receipt.removedCount === 1 ? "" : "s"}; durable browser storage is unavailable.`,
        receipt.durability === "durable" ? "success" : "warning"
      );
    } catch (error) {
      setClearReceipt(null);
      onShowSnackbar("Failed to clear and verify local data", "error");
    } finally {
      setClearingLocalData(false);
    }
  };

  const receiptSummary = clearReceipt
    ? Object.entries(clearReceipt.removedByCategory)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([category, count]) => `${category}: ${count}`)
        .join(" · ")
    : "";

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") return;
    try {
      onShowSnackbar(
        "Account deletion requested. Please contact support.",
        "info"
      );
      setShowDeleteDialog(false);
      setDeleteConfirmText("");
    } catch (error) {
      onShowSnackbar("Failed to process request", "error");
    }
  };

  const handleSignOut = async () => {
    try {
      const { data } = await supabase.auth.getUser();
      clearUserPersistence(data.user?.id ?? null);
      await supabase.auth.signOut();
      window.location.href = "/login";
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  return (
    <>
      <Grid container spacing={3}>
        {isAdmin && (
          <Grid item xs={12} md={6}>
            <Card sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                <DownloadIcon sx={{ mr: 1, verticalAlign: "middle" }} />
                Export Your Data
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Download all your data including clients, leads, activities, and
                saved searches as a JSON file.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={exportData}
              >
                Export Data
              </Button>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Card sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              <StorageIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Clear Local Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Clear this account's browser-saved preferences, Search drafts,
              unsent compose text, cached results, selections, and scroll
              positions. Server account data and sign-in stay intact.
            </Typography>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<DeleteIcon />}
                onClick={() => setShowClearDialog(true)}
                disabled={clearingLocalData}
              >
              {clearingLocalData ? "Clearing…" : "Clear Local Data"}
              </Button>
              {clearReceipt && (
                <Alert
                  severity={clearReceipt.complete ? "success" : "error"}
                  sx={{ mt: 2 }}
                  data-testid="local-clear-receipt"
                >
                  <Typography variant="body2" fontWeight={600}>
                    {clearReceipt.complete
                      ? `Verified: ${clearReceipt.removedCount} local item${clearReceipt.removedCount === 1 ? "" : "s"} removed.`
                      : `Incomplete: ${clearReceipt.failedKeys.length} item${clearReceipt.failedKeys.length === 1 ? "" : "s"} could not be verified.`}
                  </Typography>
                  {receiptSummary && (
                    <Typography variant="caption" display="block">
                      {receiptSummary}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block">
                    {clearReceipt.complete
                      ? "Verified preserved: sign-in, device theme, other users' namespaces, and stale-write safety metadata."
                      : "Preservation could not be fully verified because local storage inspection or coordination failed; sign-in and other-user data were not intentional deletion targets."}
                  </Typography>
                </Alert>
              )}
            </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="error">
              <WarningIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Danger Zone
            </Typography>
            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: { xs: 1.25, sm: 2 },
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1">Sign Out</Typography>
                <Typography variant="body2" color="text.secondary">
                  Sign out of your account on this device
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<LogoutIcon />}
                onClick={handleSignOut}
                sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
              >
                Sign Out
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "stretch", sm: "center" },
                gap: { xs: 1.25, sm: 2 },
              }}
            >
              <Box>
                <Typography variant="subtitle1" color="error">
                  Delete Account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Permanently delete your account and all associated data
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setShowDeleteDialog(true)}
                sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
              >
                Delete Account
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={showClearDialog}
        onClose={() => !clearingLocalData && setShowClearDialog(false)}
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}>Clear browser-saved work?<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setShowClearDialog(false)} disabled={clearingLocalData}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently removes this account's local Search drafts,
            unsent compose text, cached results, selections, scroll positions,
            and preferences from this browser. It does not delete server data,
            sign you out, reset the device theme, or clear another account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClearDialog(false)} disabled={clearingLocalData}>
            Cancel
          </Button>
          <Button
            color="warning"
            variant="contained"
            onClick={clearLocalData}
            disabled={clearingLocalData}
          >
            Clear Browser Data
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main", pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setShowDeleteDialog(false)}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          <WarningIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Delete Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 3 }}>
            This action cannot be undone. All your data will be permanently
            deleted.
          </Alert>
          <Typography variant="body1" sx={{ mb: 2 }}>
            To confirm, type <strong>DELETE</strong> below:
          </Typography>
          <TextField
            fullWidth
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText !== "DELETE"}
          >
            Delete My Account
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DataPrivacyTab;
